/**
 * apiError.ts — Typed extraction of a backend error message (T810)
 * =================================================================
 *
 * The BoQ API returns structured 400/403 bodies of the shape
 * `{ error: string, code?: string }` (e.g. the depth-2 / note rules surfaced by
 * `reparent_item` → `ItemMoveError`, or section move `INVALID_MOVE`). Mutation
 * `onError` callbacks receive an unknown error (axios error), so this helper
 * narrows it WITHOUT `any`, returning a clean human message (with a sensible
 * fallback) plus the optional machine `code`.
 */

/** Narrowed shape of an axios-style error carrying a DRF error body. */
interface ApiErrorBody {
  error?: unknown;
  detail?: unknown;
  code?: unknown;
}

interface AxiosLikeError {
  response?: { data?: unknown };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Extract `{ message, code }` from an unknown (axios) error. `message` falls
 * back to `fallback` when the backend body has no readable string; `code` is the
 * stable machine code when present (e.g. `MAX_DEPTH`, `NOTE_CANNOT_BE_CHILD`).
 */
export function extractApiError(
  err: unknown,
  fallback: string,
): { message: string; code: string | null } {
  let body: ApiErrorBody | undefined;
  if (isRecord(err) && isRecord((err as AxiosLikeError).response)) {
    const data = (err as AxiosLikeError).response?.data;
    if (isRecord(data)) body = data as ApiErrorBody;
  }

  const backendMessage =
    (typeof body?.error === 'string' && body.error) ||
    (typeof body?.detail === 'string' && body.detail) ||
    fallback;
  const code = typeof body?.code === 'string' ? body.code : null;
  // T2214: znana strojna koda se prevede prek privzetega prevajalnika (T1414),
  // kot v `normalizeApiError()`; sicer je ~25 klicateljev kazalo surovo
  // (slovensko) besedilo zaledja v vseh jezikih. Brez prevoda ostane besedilo
  // zaledja. `privzetiPrevajalnik` je spodaj v modulu — ob klicu je že
  // inicializiran.
  const message = prevedi(code, privzetiPrevajalnik, body as Record<string, unknown> | undefined, backendMessage);
  return { message, code };
}

/**
 * T2471: ali je zahtevek padel brez odgovora strežnika (ni povezave)?
 *
 * Axios brez odgovora (DevTools Offline, izpad omrežja) vrne napako brez
 * `response` in s kodo `ERR_NETWORK`. Tak izid uporabniku pove nekaj
 * drugega kot 400/403/5xx — sprememba sploh ni prišla do strežnika — zato ga
 * okna pravil javijo s svojim sporočilom. Napaka z odgovorom NI omrežna.
 */
export function isNetworkError(err: unknown): boolean {
  if (!isRecord(err) || isRecord((err as AxiosLikeError).response)) return false;
  return (err as { code?: unknown }).code === 'ERR_NETWORK' || (typeof navigator !== 'undefined' && navigator.onLine === false);
}

/** Minimalna axios-podobna oblika napake za ad-hoc dostop do polj (T914). */
export interface ApiError {
  message?: string;
  response?: {
    status?: number;
    data?: { detail?: string; error?: string; code?: string; [key: string]: unknown };
  };
}

/**
 * Zoži `unknown` napako iz `try/catch` v `ApiError` brez `any` (T914). Dostop do
 * polj ostane varen prek optional chaininga tudi, če napaka ni axios izvora —
 * runtime obnašanje je enako prejšnjemu `catch (err: any)`.
 */
export function asApiError(err: unknown): ApiError {
  return (err ?? {}) as ApiError;
}

/**
 * Ali je napaka **403 — nimaš pravice** (T1336 b)?
 *
 * Zakaj to potrebuje svojo funkcijo: zaslon, ki padlo poizvedbo obravnava
 * enotno (`QueryErrorState` iz T978), 403 predstavi kot okvaro nalaganja in
 * ponudi »Poskusi znova« — gumb, ki bo pri manjkajoči pravici **vedno** vrnil
 * isto. 403 ni prehodna napaka in ga je zato treba ločiti od 5xx/omrežne.
 *
 * Namenoma **ne** pokriva 401: tam odjemalec osveži žeton (`api.ts`
 * interceptor) in uporabnika po potrebi odjavi — to ni sporočilo zaslona.
 */
export function isForbiddenError(err: unknown): boolean {
  return asApiError(err)?.response?.status === 403;
}

/* ─── T1198: validacijske podrobnosti po poljih ──────────────────────────── */

/** Sporočila enega polja, kot jih vrne DRF (`{"location": ["..."]}`). */
export interface ApiFieldError {
  /** Ime polja; ugnezdena polja so sploščena v pot s pikami (`items.0.name`). */
  field: string;
  messages: string[];
}

/**
 * Prevajalnik strojne kode v uporabniku namenjeno sporočilo (T1371).
 *
 * Vrne prevedeno besedilo ali prazno vrednost, kadar koda prevoda nima —
 * natanko obnašanje i18next klica `t('<ns>.' + code, '')`.
 */
export type TranslateApiErrorCode = (
  code: string,
  params?: Record<string, string | number>,
) => string | null | undefined;

/**
 * Ključi telesa, ki niso interpolacijski parametri (T2222): sporočilo in koda
 * sama ter možnosti i18next, ki jih polje odgovora ne sme prepisati (`lng`
 * bi zamenjal jezik, `defaultValue` bi neznani kodi vrnil besedilo …).
 */
const NE_PARAMETRI = new Set([
  'error',
  'detail',
  'code',
  'details',
  'lng',
  'lngs',
  'ns',
  'defaultValue',
  'context',
  'count',
  'ordinal',
  'replace',
  'returnObjects',
  'returnDetails',
  'joinArrays',
  'postProcess',
  'interpolation',
  'keySeparator',
  'nsSeparator',
  'fallbackLng',
]);

/**
 * Pod katerim imenom prevod pričakuje polje odgovora (T2222).
 *
 * Zaledje rok pošlje kot `expires_in_hours` (pove enoto, T1384), prevodi pa
 * ga berejo kot `{{hours}}` — oba zaslona ponovnega odprtja ga tako podajata
 * že od T1384. Preslikava je ena in izrecna, ne ugibanje po imenih.
 */
const PARAMETER_PREVODA: Record<string, string> = {
  expires_in_hours: 'hours',
};

/** Skalarna polja telesa odgovora kot interpolacijske vrednosti (T2222). */
function parametriIzTelesa(body: Record<string, unknown> | undefined): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  if (!body) return out;
  for (const [key, value] of Object.entries(body)) {
    if (NE_PARAMETRI.has(key)) continue;
    if (typeof value !== 'string' && typeof value !== 'number') continue;
    out[key] = value;
    const alias = PARAMETER_PREVODA[key];
    if (alias && !(alias in body)) out[alias] = value;
  }
  return out;
}

/**
 * Prevede kodo s parametri iz telesa; sicer vrne besedilo zaledja (T2222).
 *
 * Prevod, ki po interpolaciji še nosi `{{…}}`, je **slabši** od besedila
 * zaledja (to ima vrednost že vstavljeno) — zato tak prevod zavržemo. To
 * varuje tudi pred prevajalnikom, ki parametrov ne upošteva.
 */
function prevedi(
  code: string | null,
  prevajalnik: TranslateApiErrorCode | null | undefined,
  body: Record<string, unknown> | undefined,
  backendMessage: string,
): string {
  if (!code || !prevajalnik) return backendMessage;
  const translated = prevajalnik(code, parametriIzTelesa(body));
  if (typeof translated !== 'string' || !translated) return backendMessage;
  if (/\{\{\s*\w+\s*\}\}/.test(translated)) return backendMessage;
  return translated;
}

/** Najmanjša oblika i18next instance, ki jo potrebuje privzeti prevajalnik. */
interface I18nLike {
  t: (key: string, options: Record<string, unknown>) => unknown;
}

/**
 * Privzeti prevajalnik iz i18next instance (T1414, T2222).
 *
 * Parametri gredo v `replace`, ne med možnosti, zato polje odgovora ne more
 * spremeniti jezika ali imenskega prostora. Zaprtje bere instanco ob vsakem
 * klicu, torej sledi zamenjavi jezika. Uporablja ga `main.tsx`.
 */
export function prevajalnikApiNapakIzI18n(i18n: I18nLike): TranslateApiErrorCode {
  return (code, params) => {
    const v = i18n.t(`api_errors.${code}`, { defaultValue: '', replace: params ?? {} });
    return typeof v === 'string' ? v : '';
  };
}

/**
 * Privzeti prevajalnik kod, registriran **enkrat** ob zagonu (T1414).
 *
 * **Zakaj registracija in ne uvoz `i18n`:** ta modul je čista funkcija brez
 * odvisnosti od React konteksta in od tega, ali je i18n že inicializiran —
 * obstoječe zbirke si ustvarjajo lastne instance i18next. Registracija ga pusti
 * testabilnega in hkrati odpravi vzrok: `translateCode` je bil neobvezen, zato
 * ga je od ~20 klicateljev v izdelku podalo **šest**, in vsaka nova strojna
 * koda v zaledju je bila za uporabnika nevidna.
 */
let privzetiPrevajalnik: TranslateApiErrorCode | null = null;

/** Registrira (ali z `null` odstrani) privzeti prevajalnik strojnih kod. */
export function setDefaultApiErrorTranslator(fn: TranslateApiErrorCode | null): void {
  privzetiPrevajalnik = fn;
}

export interface NormalizedApiError {
  /** Berljivo splošno sporočilo (`error` / `detail` / fallback). */
  message: string;
  /** Strojna koda (`VALIDATION_ERROR`, `PROJECT_LIMIT_REACHED`, …). */
  code: string | null;
  /** Sporočila po poljih; prazno, kadar odgovor ni validacijski. */
  fieldErrors: ApiFieldError[];
}

/** Ključi telesa napake, ki niso imena polj. */
const NON_FIELD_KEYS = new Set(['error', 'detail', 'code', 'details']);

function toMessages(value: unknown): string[] {
  if (typeof value === 'string') return value ? [value] : [];
  if (Array.isArray(value)) {
    return value.filter((v): v is string => typeof v === 'string' && v.length > 0);
  }
  return [];
}

/**
 * Rekurzivno sploščí `details` v seznam `{field, messages}`. Ugnezdene strukture
 * (serializerji z `many=True` ali vgnezdenimi serializerji) dobijo pot s pikami,
 * da uporabnik vidi, katera postavka je sporna.
 */
function flattenFieldErrors(value: unknown, prefix = ''): ApiFieldError[] {
  if (!isRecord(value)) return [];
  const out: ApiFieldError[] = [];
  for (const [key, raw] of Object.entries(value)) {
    const field = prefix ? `${prefix}.${key}` : key;
    const messages = toMessages(raw);
    if (messages.length > 0) {
      out.push({ field, messages });
    } else if (isRecord(raw) || Array.isArray(raw)) {
      out.push(...flattenFieldErrors(Array.isArray(raw) ? { ...raw } : raw, field));
    }
  }
  return out;
}

/**
 * Normalizira poljubno (axios) napako v `{message, code, fieldErrors}` (T1198).
 *
 * Backend ob validacijski napaki vrne `{error, code: "VALIDATION_ERROR",
 * details: {<polje>: ["<sporočilo>"]}}`. Obrazci so doslej izrisali samo
 * `error` — uporabnik je videl goli »Napaka pri validaciji.« in ni izvedel,
 * katero polje je sporno. Podprt je tudi surov DRF `ValidationError` brez ovoja
 * `details`, ki ga vračajo generični DRF pogledi.
 *
 * T1371: neobvezni `translateCode` prevede strojno kodo v sporočilo, ki pove
 * vzrok (`TOO_LARGE` → »Datoteka je prevelika …«). Brez njega se vedenje ne
 * spremeni, zato ostali klicatelji ostanejo nedotaknjeni.
 */
export function normalizeApiError(
  err: unknown,
  fallback: string,
  translateCode?: TranslateApiErrorCode,
): NormalizedApiError {
  let body: Record<string, unknown> | undefined;
  if (isRecord(err) && isRecord((err as AxiosLikeError).response)) {
    const data = (err as AxiosLikeError).response?.data;
    if (isRecord(data)) body = data;
  }

  const backendMessage =
    (typeof body?.error === 'string' && body.error) ||
    (typeof body?.detail === 'string' && body.detail) ||
    fallback;
  const code = typeof body?.code === 'string' ? body.code : null;

  // T1371: strojna koda pove, KAJ je narobe; sporočilo zaledja pogosto ne
  // (»Napaka pri validaciji.« ob 10 MB datoteki). Kadar odjemalec za kodo
  // pozna prevod, ta prevlada — sicer ostane sporočilo zaledja nedotaknjeno,
  // zato klicatelji brez `translateCode` obnašanja ne spremenijo.
  // T1414: kadar klicatelj prevajalnika ne poda, velja privzeti iz registracije
  // ob zagonu. Izrecni ostane močnejši — nekateri konteksti berejo iz svojega
  // prostora imen (`import_v2.error.code.*`).
  const prevajalnik = translateCode ?? privzetiPrevajalnik;
  const message = prevedi(code, prevajalnik, body, backendMessage);

  let fieldErrors: ApiFieldError[] = [];
  if (body) {
    if (isRecord(body.details)) {
      fieldErrors = flattenFieldErrors(body.details);
    } else if (!code) {
      // Surov DRF ValidationError: telo je kar preslikava polje → sporočila.
      // T2955: samo brez strojne kode. Ob kodi so ostala polja parametri
      // (`missing`, `state`, `limit`), ne sporna polja — sicer je uporabnik
      // bral »missing: bio specializations«.
      const withoutMeta = Object.fromEntries(
        Object.entries(body).filter(([key]) => !NON_FIELD_KEYS.has(key)),
      );
      fieldErrors = flattenFieldErrors(withoutMeta);
    }
  }

  return { message, code, fieldErrors };
}

/**
 * Kot `normalizeApiError`, a razpakira telo, ki je prišlo kot `Blob` (T1877).
 *
 * **Zakaj obstaja.** Klic z `responseType: 'blob'` (predogled predloge vrne
 * PDF) dobi v `Blob` zavito tudi telo **napake**. `normalizeApiError` nanj ne
 * prime, ker je `typeof Blob === 'object'`: `isRecord()` vrne `true`, `error`
 * in `detail` pa sta `undefined`, zato tiho ostane rezervno besedilo. Do
 * 18.9.2026 je to pomenilo, da je sysadminov predogled na vsako napako
 * svetoval »Preverite HTML predlogo« — tudi takrat, ko je bil HTML pravilen in
 * je manjkal vzorčni kontekst (T1876).
 *
 * **Zakaj je pomočnik in ne popravek na klicnem mestu.** Ista logika je od
 * prej vpisana na roko v `TemplatesPage.handlePreview()`; dve kopiji istega
 * razpakiranja sta natanko razlog, da ga eno klicno mesto ima in drugo ne.
 *
 * Funkcija je `async`, ker je `Blob.text()` asinhron. Telo, ki ni JSON
 * (proxyjeva HTML stran ob 502), pusti rezervno besedilo nedotaknjeno.
 */
export async function normalizeBlobApiError(
  err: unknown,
  fallback: string,
  translateCode?: TranslateApiErrorCode,
): Promise<NormalizedApiError> {
  const data = isRecord(err) ? (err as AxiosLikeError).response?.data : undefined;
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    try {
      const parsed: unknown = JSON.parse(await data.text());
      if (isRecord(parsed)) {
        return normalizeApiError(
          { response: { ...(err as AxiosLikeError).response, data: parsed } },
          fallback,
          translateCode,
        );
      }
    } catch {
      // Telo ni JSON (npr. HTML stran posrednika) — rezervno besedilo je
      // edino, kar lahko povemo, in je boljše od surovega HTML na zaslonu.
    }
    return { message: fallback, code: null, fieldErrors: [] };
  }
  return normalizeApiError(err, fallback, translateCode);
}
